import React, { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, Send, HeartHandshake } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { companyInfo, services } from "../data/mockData";
import { submitInquiry } from "../lib/api";

const initialForm = {
  name: "",
  email: "",
  phone: "",
  recipientRelation: "",
  service: "",
  hoursNeeded: "",
  timeframe: "",
  message: "",
  consent: false
};

const Contact = () => {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    if (!form.consent) {
      toast.error("Please agree to be contacted before submitting.");
      return;
    }

    setSubmitting(true);
    try {
      await submitInquiry({
        name: form.name,
        email: form.email,
        phone: form.phone,
        recipient_relation: form.recipientRelation || null,
        service: form.service || null,
        hours_needed: form.hoursNeeded || null,
        timeframe: form.timeframe || null,
        message: form.message,
        consent: form.consent,
        source: "contact-form",
      });
      toast.success("Thank you! We'll reach out within 24 hours.", {
        description: "Check your inbox — we've sent a welcome note along with our free Senior Care Readiness Checklist."
      });
      setForm(initialForm);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Something went wrong. Please call us directly.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-stone-50">
      {/* HERO */}
      <section className="relative bg-amber-50/30 py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl animate-fade-up">
            <p className="text-amber-700 text-sm uppercase tracking-[0.3em] font-semibold mb-5">
              Let's Talk
            </p>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-slate-900 leading-[1.05] tracking-tight">
              We're here to <span className="italic text-amber-700">help.</span>
            </h1>
            <p className="mt-8 text-lg lg:text-xl text-slate-600 leading-relaxed max-w-2xl">
              Every family's needs are different. Share a little about your situation and a care coordinator will reach out personally — no pressure, no obligation.
            </p>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
            {/* Info */}
            <div className="lg:col-span-4">
              <div className="bg-slate-900 text-white rounded-[2rem] p-8 lg:p-10 h-full">
                <HeartHandshake className="w-12 h-12 text-amber-400 mb-6" strokeWidth={1.5} />
                <h2 className="font-serif text-3xl text-white mb-3">
                  Reach us any time
                </h2>
                <p className="text-stone-300 mb-10 leading-relaxed">
                  A real person will respond — warmly, quickly, and without pressure.
                </p>

                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-full bg-amber-600 flex items-center justify-center flex-shrink-0">
                      <Phone className="w-5 h-5 text-white" strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className="text-xs text-amber-400 uppercase tracking-widest mb-1">
                        Phone
                      </p>
                      <a
                        href={`tel:${companyInfo.phone.replace(/\D/g, "")}`}
                        data-testid="contact-phone"
                        className="text-white text-lg hover:text-amber-400 transition-colors"
                      >
                        {companyInfo.phone}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-full bg-amber-600 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-white" strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className="text-xs text-amber-400 uppercase tracking-widest mb-1">
                        Email
                      </p>
                      <a
                        href={`mailto:${companyInfo.email}`}
                        data-testid="contact-email"
                        className="text-white hover:text-amber-400 transition-colors break-all"
                      >
                        {companyInfo.email}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-full bg-amber-600 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-white" strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className="text-xs text-amber-400 uppercase tracking-widest mb-1">
                        Service area
                      </p>
                      <p className="text-white">
                        {companyInfo.address}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-full bg-amber-600 flex items-center justify-center flex-shrink-0">
                      <Clock className="w-5 h-5 text-white" strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className="text-xs text-amber-400 uppercase tracking-widest mb-1">
                        Hours
                      </p>
                      <p className="text-white">{companyInfo.hours}</p>
                    </div>
                  </div>
                </div>

                <div className="mt-10 pt-8 border-t border-stone-800">
                  <p className="font-script text-3xl text-amber-400">
                    Because every life<br/>deserves a blessing.
                  </p>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-8">
              <div className="bg-white rounded-[2rem] p-8 lg:p-12 border border-stone-200/70">
                <h2 className="font-serif text-3xl lg:text-4xl text-slate-900 mb-3">
                  Request care for your family
                </h2>
                <p className="text-slate-600 mb-10">
                  Fill this out and we'll follow up within 24 hours. All information is kept confidential.
                </p>

                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-slate-900 font-medium">
                        Your name <span className="text-amber-700">*</span>
                      </Label>
                      <Input
                        id="name"
                        data-testid="form-input-name"
                        value={form.name}
                        onChange={(e) => handleChange("name", e.target.value)}
                        placeholder="Jane Smith"
                        className="rounded-xl border-stone-200 py-6 focus-visible:ring-amber-700"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-slate-900 font-medium">
                        Phone <span className="text-amber-700">*</span>
                      </Label>
                      <Input
                        id="phone"
                        data-testid="form-input-phone"
                        value={form.phone}
                        onChange={(e) => handleChange("phone", e.target.value)}
                        placeholder="(555) 555-5555"
                        className="rounded-xl border-stone-200 py-6 focus-visible:ring-amber-700"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-slate-900 font-medium">
                      Email <span className="text-amber-700">*</span>
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      data-testid="form-input-email"
                      value={form.email}
                      onChange={(e) => handleChange("email", e.target.value)}
                      placeholder="jane@example.com"
                      className="rounded-xl border-stone-200 py-6 focus-visible:ring-amber-700"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <Label className="text-slate-900 font-medium">Who needs care?</Label>
                      <Select value={form.recipientRelation} onValueChange={(v) => handleChange("recipientRelation", v)}>
                        <SelectTrigger
                          data-testid="form-select-recipient"
                          className="rounded-xl border-stone-200 py-6 focus:ring-amber-700"
                        >
                          <SelectValue placeholder="Select relation" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="parent">My parent</SelectItem>
                          <SelectItem value="spouse">My spouse</SelectItem>
                          <SelectItem value="myself">Myself</SelectItem>
                          <SelectItem value="grandparent">My grandparent</SelectItem>
                          <SelectItem value="other">Other family member</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-900 font-medium">Services of interest</Label>
                      <Select value={form.service} onValueChange={(v) => handleChange("service", v)}>
                        <SelectTrigger
                          data-testid="form-select-service"
                          className="rounded-xl border-stone-200 py-6 focus:ring-amber-700"
                        >
                          <SelectValue placeholder="Select a service" />
                        </SelectTrigger>
                        <SelectContent>
                          {services.map((s) => (
                            <SelectItem key={s.id} value={s.id}>{s.title}</SelectItem>
                          ))}
                          <SelectItem value="multiple">Multiple / not sure</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <Label className="text-slate-900 font-medium">Hours per week</Label>
                      <Select value={form.hoursNeeded} onValueChange={(v) => handleChange("hoursNeeded", v)}>
                        <SelectTrigger
                          data-testid="form-select-hours"
                          className="rounded-xl border-stone-200 py-6 focus:ring-amber-700"
                        >
                          <SelectValue placeholder="How many hours?" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1-10">1 to 10 hours</SelectItem>
                          <SelectItem value="10-20">10 to 20 hours</SelectItem>
                          <SelectItem value="20-40">20 to 40 hours</SelectItem>
                          <SelectItem value="40+">40+ hours</SelectItem>
                          <SelectItem value="24-7">24/7 live-in</SelectItem>
                          <SelectItem value="not-sure">Not sure yet</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-900 font-medium">When do you need care?</Label>
                      <Select value={form.timeframe} onValueChange={(v) => handleChange("timeframe", v)}>
                        <SelectTrigger
                          data-testid="form-select-timeframe"
                          className="rounded-xl border-stone-200 py-6 focus:ring-amber-700"
                        >
                          <SelectValue placeholder="Choose a timeframe" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="immediately">Immediately</SelectItem>
                          <SelectItem value="1-2weeks">Within 1-2 weeks</SelectItem>
                          <SelectItem value="1month">Within a month</SelectItem>
                          <SelectItem value="planning">Just planning ahead</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-slate-900 font-medium">
                      Tell us about your loved one <span className="text-amber-700">*</span>
                    </Label>
                    <Textarea
                      id="message"
                      data-testid="form-input-message"
                      value={form.message}
                      onChange={(e) => handleChange("message", e.target.value)}
                      placeholder="Share anything you'd like us to know — their needs, their personality, your questions. We're here to listen."
                      rows={5}
                      className="rounded-xl border-stone-200 focus-visible:ring-amber-700 resize-none"
                    />
                  </div>

                  <div className="flex items-start gap-3 pt-2">
                    <Checkbox
                      id="consent"
                      data-testid="form-checkbox-consent"
                      checked={form.consent}
                      onCheckedChange={(v) => handleChange("consent", v)}
                      className="mt-1 border-stone-400 data-[state=checked]:bg-amber-700 data-[state=checked]:border-amber-700"
                    />
                    <Label htmlFor="consent" className="text-sm text-slate-600 leading-relaxed cursor-pointer">
                      I agree to be contacted by a care coordinator. Your information stays private and is never shared.
                    </Label>
                  </div>

                  <Button
                    type="submit"
                    data-testid="form-submit-button"
                    disabled={submitting}
                    className="w-full bg-amber-700 hover:bg-amber-800 text-white rounded-full py-7 text-base font-medium mt-4"
                  >
                    {submitting ? (
                      "Sending..."
                    ) : (
                      <>
                        Send my request
                        <Send className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
